// src/components/SearchBarWithMaya.js
import React, { useState, useRef, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CircularProgress from '@mui/material/CircularProgress';
import pb from '../lib/pb';

export default function SearchBarWithMaya({ placeholder = "Search or ask Maya...", onResults }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showMaya, setShowMaya] = useState(false);
  const [mayaMessages, setMayaMessages] = useState([
    { from: 'maya', text: "Hi, I'm Maya (v1) � your AI assistant. How can I help today?" },
  ]);
  const [mayaInput, setMayaInput] = useState('');

  const mayaBodyRef = useRef(null);

  // Auto-scroll Maya chat downwards
  useEffect(() => {
    if (mayaBodyRef.current)
      mayaBodyRef.current.scrollTop = mayaBodyRef.current.scrollHeight;
  }, [mayaMessages]);

  // ?? Basic platform-wide realtime search (PocketBase)
  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);

    try {
      const users = await pb.collection('users').getFullList({
        filter: `username ~ "${query}" || bio ~ "${query}" || city ~ "${query}"`,
        expand: "followers"
      });
      const posts = await pb.collection('posts').getFullList({
        filter: `title ~ "${query}" || body ~ "${query}"`,
        expand: "author"
      });
      const results = { users, posts };
      onResults?.(results);
      setSuggestions([...users.map(u=>u.username), ...posts.map(p=>p.title)]);
    } catch (err) {
      console.error("Search failed", err);
    } finally {
      setLoading(false);
    }
  };

  // ?? Maya AI Assistant Logic
  const handleMayaAsk = async (inputText) => {
    const q = inputText || query;
    if (!q.trim()) return;
    setAiThinking(true);
    const userMessage = { from: 'user', text: q };
    setMayaMessages(prev => [...prev, userMessage]);
    setQuery('');
    setMayaInput('');

    try {
      // ?? Save chat log in PocketBase for now
      await pb.collection('ai_logs').create({
        query: q,
        source: 'Maya-v1',
        createdAt: new Date().toISOString(),
      });

      // ?? Placeholder LLM simulation
      // Later this connects to your open-source LLM (Ollama/Mistral/Llama2 etc.)
      const simulatedResponse = [
        "I'm analyzing your query using platform data sources.",
        "Soon, I'll generate expert-level insights here with full citations!"
      ];
      const randomMsg = simulatedResponse[Math.floor(Math.random() * simulatedResponse.length)];

      setMayaMessages(prev => [...prev, { from: 'maya', text: randomMsg }]);
    } catch (err) {
      console.error("Maya AI failed", err);
      setMayaMessages(prev => [...prev, { from: 'maya', text: "Oops! I had trouble processing that. Try again later." }]);
    } finally {
      setAiThinking(false);
    }
  };

  // ?? Handle enter in search or Maya mode
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showMaya) handleMayaAsk(mayaInput);
      else handleSearch();
    }
  };

  return (
    <div className="maya-search-container">
      {/* ?? Main unified Search Bar */}
      <div className="searchbar-with-maya">
        <input
          type="text"
          value={query}
          onChange={(e)=>setQuery(e.target.value)}
          placeholder={placeholder}
          onKeyDown={handleKeyPress}
        />
        {loading ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <SearchIcon onClick={handleSearch} className="icon" titleAccess="Search" />
        )}

        <div className="mx-2 w-px bg-gray-300 h-5"></div>

        {aiThinking ? (
          <CircularProgress size={18} color="secondary" />
        ) : (
          <button className="maya-button" onClick={()=>setShowMaya(!showMaya)}>
            <SmartToyIcon style={{ fontSize: 18 }} /> Maya
          </button>
        )}

        {/* ?? Suggestions dropdown */}
        {suggestions.length > 0 && (
          <div className="absolute top-10 left-0 right-0 bg-gray-900 rounded-md border border-gray-700 shadow-lg z-50 max-h-64 overflow-auto">
            {suggestions.map((s, i)=>(
              <div
                key={i}
                onClick={()=>{ setQuery(s); handleSearch(); }}
                className="px-3 py-1 hover:bg-gray-700 cursor-pointer text-sm"
              >
                {s}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ?? Maya Chat Modal */}
      {showMaya && (
        <div className="maya-chat-modal">
          <div className="maya-chat-header">
            <span>?? Maya (v1)</span>
            <button onClick={()=>setShowMaya(false)}>?</button>
          </div>
          <div className="maya-chat-body" ref={mayaBodyRef}>
            {mayaMessages.map((m, i)=>(
              <div key={i} className={`msg ${m.from}`}>{m.text}</div>
            ))}
          </div>
          <div className="maya-chat-input">
            <input
              value={mayaInput}
              onChange={e=>setMayaInput(e.target.value)}
              placeholder="Type your message..."
              onKeyDown={handleKeyPress}
            />
            <button onClick={()=>handleMayaAsk(mayaInput)} disabled={aiThinking}>
              {aiThinking ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
